import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { complianceApi, reportsApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, TrendingUp, CheckCircle2, AlertTriangle, BarChart3 } from 'lucide-react';
import { LogoHome } from '../components/LogoHome';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e3b3c70d-c72e-49dc-b41a-da6affdc6e46/artifacts/4w4rfom9_IMG_0777.PNG";

export default function CompliancePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [reports, setReports] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [metricsRes, reportsRes] = await Promise.all([
        complianceApi.getMetrics(),
        reportsApi.getAll()
      ]);
      setMetrics(metricsRes.data);
      setReports(reportsRes.data.filter(r => r.compliance_score));
    } catch (error) {
      console.error('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 85) return 'text-green-600';
    if (score >= 70) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBadge = (score) => {
    if (score >= 85) return { label: 'Excellent', variant: 'default', className: 'bg-green-100 text-green-700' };
    if (score >= 70) return { label: 'Good', variant: 'secondary', className: 'bg-amber-100 text-amber-700' };
    return { label: 'Needs Review', variant: 'destructive', className: 'bg-red-100 text-red-700' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="compliance-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-slate-200" />
              <h1 className="font-heading font-semibold text-slate-900">Compliance Metrics</h1>
            </div>
            <LogoHome className="h-12 w-auto" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Average Score</p>
                  <p className={`text-3xl font-bold ${getScoreColor(metrics?.average_score || 0)}`}>
                    {Math.round(metrics?.average_score || 0)}%
                  </p>
                </div>
                <div className="p-3 bg-teal-100 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-teal-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Reports</p>
                  <p className="text-3xl font-bold text-slate-900">{metrics?.total_reports || 0}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <BarChart3 className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">High Compliance</p>
                  <p className="text-3xl font-bold text-green-600">{metrics?.high_compliance || 0}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Needs Review</p>
                  <p className="text-3xl font-bold text-red-600">{metrics?.needs_improvement || 0}</p>
                </div>
                <div className="p-3 bg-red-100 rounded-lg">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Score Breakdown */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Compliance Score Breakdown</CardTitle>
                <CardDescription>NDIS documentation compliance by report</CardDescription>
              </CardHeader>
              <CardContent>
                {reports.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <BarChart3 className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No compliance data yet. Generate AI reports to see scores.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reports.map((report) => {
                      const badge = getScoreBadge(report.compliance_score);
                      return (
                        <div key={report.id} className="p-4 rounded-lg border border-slate-200 hover:border-teal-500/50 transition-colors">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <p className="font-medium text-slate-900 capitalize">
                                {report.template_type.replace(/_/g, ' ')}
                              </p>
                              <p className="text-sm text-slate-500">
                                {new Date(report.created_at).toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney', day: 'numeric', month: 'short', year: 'numeric' })}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className={`text-2xl font-bold ${getScoreColor(report.compliance_score)}`}>
                                {report.compliance_score}%
                              </p>
                              <Badge className={badge.className}>{badge.label}</Badge>
                            </div>
                          </div>
                          <Progress value={report.compliance_score} className="h-2" />
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Compliance Tips */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-base">Improve Your Score</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 rounded-lg bg-teal-50 border border-teal-100">
                  <h4 className="font-medium text-teal-900 text-sm mb-1">Use Person-First Language</h4>
                  <p className="text-xs text-teal-700">Always refer to participants by name and focus on their strengths.</p>
                </div>
                <div className="p-3 rounded-lg bg-teal-50 border border-teal-100">
                  <h4 className="font-medium text-teal-900 text-sm mb-1">Be Specific & Measurable</h4>
                  <p className="text-xs text-teal-700">Include specific observations and measurable progress indicators.</p>
                </div>
                <div className="p-3 rounded-lg bg-teal-50 border border-teal-100">
                  <h4 className="font-medium text-teal-900 text-sm mb-1">Link to NDIS Goals</h4>
                  <p className="text-xs text-teal-700">Connect activities and observations to the participant's NDIS goals.</p>
                </div>
                <div className="p-3 rounded-lg bg-teal-50 border border-teal-100">
                  <h4 className="font-medium text-teal-900 text-sm mb-1">Cultural Considerations</h4>
                  <p className="text-xs text-teal-700">Include culturally appropriate observations and respect preferences.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
